import { CheckCircleOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';
import React, { useEffect, useState } from 'react';
import { green } from '@ant-design/colors';
import { yamlToJson } from '../../../utils';
import TestConnectionModal from './TestConnectionModal';
import { SourceConfig } from '../../types';
import { RECIPE_FIELDS } from '../constants';
import { RecipeField } from '../common';
import { useTranslation } from '../../../../../../i18n/useTranslation';
import { useTestConnectionPolling } from './TestConnectionPollingContextAPIs';

export function getRecipeJson(recipeYaml: string) {
    // Convert the recipe into it's json representation, and catch + report exceptions while we do it.
    let recipeJson;
    try {
        recipeJson = yamlToJson(recipeYaml);
    } catch (e) {
        const messageText = (e as any).parsedLine
            ? `Please fix line ${(e as any).parsedLine} in your recipe.`
            : 'Please check your recipe configuration.';
        message.warn(`Found invalid YAML. ${messageText}`);
        return null;
    }
    return recipeJson;
}

interface Props {
    type: string;
    recipe: string;
    applianceId: string;
    sourceConfigs?: SourceConfig;
    version?: string | null;
    form?: any;
}

function TestConnectionButton(props: Props) {
    const { t } = useTranslation();
    const { recipe, sourceConfigs, version, applianceId, type, form } = props;

    const {
        isLoading,
        setIsLoading,
        testConnectionResult,
        setTestConnectionFailureCheck,
        createTestConnectionRequest,
        getIngestionExecutionRequest,
        setShowTestConnectionResultCollapsible,
        showTestConnectionResultCollapsible,
        isModalVisible,
        setIsModalVisible,
    } = useTestConnectionPolling();

    const [isDisabled, setIsDisabled] = useState(true);

    const CloseModal = () => {
        setIsModalVisible(false);
        setTestConnectionFailureCheck(false);
    };
    // Check if connection fields are filled
    useEffect(() => {
        if (!form || !applianceId) {
            setIsDisabled(true);
            return;
        }

        // Check if all required connection fields have values
        try {
            const { fields } = RECIPE_FIELDS[type as string];

            // Filter for required fields only
            const requiredFields = fields.filter((field: RecipeField) => field.required === true);

            // Check if all required fields have values
            const hasAllRequiredFields = requiredFields.every((field: RecipeField) => {
                const value = form.getFieldValue(field.name);
                return value !== undefined && value !== null && value !== '';
            });

            setIsDisabled(!hasAllRequiredFields || !applianceId);
        } catch (e) {
            // If there's an error in the check, disable the button
            setIsDisabled(true);
        }
    }, [form, applianceId, type, recipe]);

    function testConnection() {
        const recipeJson = getRecipeJson(recipe);
        if (recipeJson) {
            setShowTestConnectionResultCollapsible(false);
            createTestConnectionRequest({ variables: { input: { type, applianceId, recipe: recipeJson, version } } })
                .then((res) =>
                    getIngestionExecutionRequest({
                        variables: { urn: res.data?.createTestConnectionRequest as string },
                    }),
                )
                .catch(() => {
                    message.error(
                        t('There was an unexpected error when trying to test your connection. Please try again.'),
                    );
                    setShowTestConnectionResultCollapsible(false);
                    setTestConnectionFailureCheck(true);
                });

            setIsLoading(true);
            setIsModalVisible(true);
        }
    }
    const [testConnectionFailed, setTestConnectionFailed] = useState(false);

    useEffect(() => {
        if (testConnectionResult && showTestConnectionResultCollapsible) {
            const internalFailure = !!testConnectionResult?.internal_failure;
            const basicConnectivityFailure = testConnectionResult?.basic_connectivity?.capable === false;
            const testConnectionFailedVar = internalFailure || basicConnectivityFailure;
            setTestConnectionFailed(testConnectionFailedVar);
        }
    }, [testConnectionResult, showTestConnectionResultCollapsible]);

    useEffect(() => {
        if (isModalVisible && testConnectionFailed) {
            setIsModalVisible(false);
        }
    }, [isModalVisible, testConnectionFailed, setIsModalVisible]);

    return (
        <>
            <Button
                onClick={testConnection}
                disabled={isDisabled}
                aria-label={t('Test Connection')}
                loading={isLoading}
            >
                <CheckCircleOutlined style={{ color: green[5] }} />
                {t('Test Connection')}
            </Button>
            {isModalVisible && (
                <TestConnectionModal
                    isLoading={isLoading}
                    testConnectionFailed={testConnectionFailed}
                    sourceConfig={sourceConfigs}
                    testConnectionResult={testConnectionResult}
                    hideModal={CloseModal}
                />
            )}
        </>
    );
}

export default TestConnectionButton;
